/* GASSISA
=========================================================================================
* Descripción: Seguimiento de tickets de soporte del Tenant (canal TenantTickets de
*              Seguridad, misma BD maestra que SupportSizeSoftX). Lista "Mis solicitudes",
*              detalle con conversación/historial, respuesta con evidencias y descarga de
*              adjuntos. Sin Kanban administrativo: el Tenant no cambia estados ni asigna.
*              Vive dentro del menú Sistema (/registrofallos); no crea un menú duplicado.
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 22/09/2026 | Lic. José Luis Aragón Cervantes  | Creación: seguimiento Tenant alineado a Support
* ---------------------------------------------------------------------------------------
*/

import TextField from "../ui/TextField";
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

import { useRequest } from "../../context/System/RequestContext";
import { useLayout } from "../../context/System/LayoutContext";
import { useSesion } from "../../context/System/SesionContext";
import {
  apiSoporteMisTickets,
  apiSoporteTicketDetalle,
  apiSoporteTicketResponder,
  apiSoporteTicketLeer,
  apiSoporteTicketAdjunto,
} from "../../settings/ApiConfig";
import { getAuthorizationToken } from "../../settings/sessionStorage";

// Etiquetas alineadas al contrato Support (SupportTickets): mismos estados,
// prioridades y tipos. El Tenant solo visualiza; no gestiona.
const STATUS_LABEL = {
  NUEVO: "Nuevo",
  EN_ATENCION: "En atención",
  EN_ESPERA: "En espera",
  RESUELTO: "Resuelto",
  CERRADO: "Cerrado",
};
const STATUS_COLOR = {
  NUEVO: "info",
  EN_ATENCION: "primary",
  EN_ESPERA: "warning",
  RESUELTO: "success",
  CERRADO: "default",
};
const TIPO_LABEL = {
  FALLO: "Fallo del sistema",
  ASISTENCIA: "Asistencia técnica",
  SOLICITUD: "Solicitud",
};

// Límites idénticos al backend (TenantTicketsController.LeerAdjuntos).
const MAX_ARCHIVOS = 5;
const MAX_BYTES = 8 * 1024 * 1024;
const ACCEPT = "image/png,image/jpeg,image/webp,image/gif,application/pdf";

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.tickets)) return value.tickets;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

const getLocalDate = (value) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

export default function TicketsSeguimiento({ refreshKey = 0 }) {
  const { GetRequest, PostRequest } = useRequest();
  const { handleOpenAlert } = useLayout();
  const { tipoUsuario, usuarioData } = useSesion();

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [detail, setDetail] = useState(null);
  const [opening, setOpening] = useState(false);
  const [tab, setTab] = useState("conversacion");
  const [reply, setReply] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);

  const load = async (withLoader = true) => {
    setLoading(true);
    try {
      const resp = await GetRequest({
        url: apiSoporteMisTickets,
        loader: withLoader ? "Cargando mis tickets..." : undefined,
      });
      if (resp?.hasError) {
        if (withLoader) handleOpenAlert(resp.message || "No se pudieron cargar los tickets", "error");
        return;
      }
      setTickets(asArray(resp?.result));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(true);
  }, [refreshKey]);

  const filtered = useMemo(() => {
    const text = query.trim().toLowerCase();
    const list = Array.isArray(tickets) ? tickets : [];
    if (!text) return list;
    return list.filter((t) =>
      `${t.folio || ""} ${t.titulo || ""} ${t.categoria || ""} ${t.status || ""}`
        .toLowerCase()
        .includes(text)
    );
  }, [tickets, query]);

  const unreadCount = useMemo(
    () => (Array.isArray(tickets) ? tickets.filter((t) => t.noLeido).length : 0),
    [tickets]
  );

  const openDetail = async (id) => {
    setOpening(true);
    try {
      const resp = await GetRequest({
        url: apiSoporteTicketDetalle(id),
        loader: "Abriendo ticket...",
      });
      if (resp?.hasError) {
        handleOpenAlert(resp.message || "No se pudo abrir el ticket", "error");
        return;
      }
      setDetail(resp.result);
      setTab("conversacion");
      setReply("");
      setFiles([]);
      // Marca lectura en el servidor y refresca insignias sin loader.
      try {
        await PostRequest({ url: apiSoporteTicketLeer(id), body: {} });
      } catch {}
      load(false);
    } finally {
      setOpening(false);
    }
  };

  const closeDetail = () => {
    setDetail(null);
    setReply("");
    setFiles([]);
  };

  const validateFiles = (list) => {
    const arr = Array.from(list || []);
    if (arr.length > MAX_ARCHIVOS) {
      handleOpenAlert("Seleccione hasta cinco evidencias.", "error");
      return null;
    }
    for (const f of arr) {
      if (!f || f.size <= 0 || f.size > MAX_BYTES) {
        handleOpenAlert(`"${f?.name || "archivo"}" debe pesar entre 1 byte y 8 MB.`, "error");
        return null;
      }
      const mime = String(f.type || "").toLowerCase();
      if (!["image/png", "image/jpeg", "image/webp", "image/gif", "application/pdf"].includes(mime)) {
        handleOpenAlert(`"${f.name}": use imégenes PNG, JPG, WEBP, GIF o PDF.`, "error");
        return null;
      }
    }
    return arr;
  };

  const sendReply = async (e) => {
    e?.preventDefault?.();
    if (!detail || sending) return;
    if (!reply.trim() && !files.length) {
      handleOpenAlert("Escriba un mensaje o adjunte una evidencia.", "warning");
      return;
    }
    const fd = new FormData();
    fd.append("mensaje", reply.trim());
    fd.append("usuario", usuarioData?.Usuario || usuarioData?.usuario || "");
    fd.append("rol", tipoUsuario || "");
    files.forEach((f) => fd.append("archivos", f));
    setSending(true);
    try {
      // multipart/form-data: fetch directo con el Bearer centralizado (misma
      // precedencia JWE/JWT que RequestContext). Hereda la sesión renovada.
      const apiResp = await fetch(apiSoporteTicketResponder(detail.ticketId), {
        method: "POST",
        headers: { Authorization: "Bearer " + getAuthorizationToken() },
        body: fd,
      });
      const data = await apiResp.json().catch(() => null);
      if (!apiResp.ok || !data || data.hasError) {
        handleOpenAlert(data?.message || "No se pudo enviar la respuesta.", "error");
        return;
      }
      setDetail(data.result);
      setReply("");
      setFiles([]);
      handleOpenAlert("Respuesta enviada", "OK");
      load(false);
    } catch (err) {
      handleOpenAlert("Error al enviar respuesta: " + (err?.message || err), "error");
    } finally {
      setSending(false);
    }
  };

  const downloadAdjunto = async (ticketId, adjunto) => {
    try {
      const r = await fetch(apiSoporteTicketAdjunto(ticketId, adjunto.adjuntoId), {
        headers: { Authorization: "Bearer " + getAuthorizationToken() },
      });
      if (!r.ok) throw new Error("No se pudo descargar el adjunto.");
      const url = URL.createObjectURL(await r.blob());
      const link = document.createElement("a");
      link.href = url;
      link.download = adjunto.nombreArchivo || "adjunto";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      handleOpenAlert(err?.message || "No se pudo descargar el adjunto.", "error");
    }
  };

  const detailAdjuntos = useMemo(() => {
    if (!detail) return { generales: [], porMensaje: new Map() };
    const generales = [];
    const porMensaje = new Map();
    (detail.adjuntos || []).forEach((a) => {
      if (!a?.mensajeId) {
        generales.push(a);
        return;
      }
      if (!porMensaje.has(a.mensajeId)) porMensaje.set(a.mensajeId, []);
      porMensaje.get(a.mensajeId).push(a);
    });
    return { generales, porMensaje };
  }, [detail]);

  return (
    <Box sx={{ display: "grid", gap: 2, alignContent: "start" }}>
      <Card>
        <CardContent sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <TextField autoComplete="off"
            label="Buscar por folio, título, categoría o estado"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            size="small"
            sx={{ minWidth: { xs: "100%", sm: 280 }, flex: 1 }}
          />
          <Chip label={`${filtered.length} ticket(s)`} color="primary" variant="outlined" />
          {unreadCount > 0 && <Chip label={`${unreadCount} con novedades`} color="warning" />}
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => load(true)}
            disabled={loading}
          >
            Actualizar
          </Button>
        </CardContent>
      </Card>

      {loading && !tickets.length ? (
        <Alert severity="info">Cargando tickets...</Alert>
      ) : !filtered.length ? (
        <Alert severity="info">
          Sin tickets. Los tickets creados desde el registro de fallos aparecen aquí para su seguimiento.
        </Alert>
      ) : (
        filtered.map((t) => (
          <Card
            key={t.ticketId}
            variant="outlined"
            sx={{ borderLeft: "3px solid", borderLeftColor: t.noLeido ? "warning.main" : "#41444b", cursor: "pointer", "&:hover": { borderColor: "#758195" } }}
            onClick={() => !opening && openDetail(t.ticketId)}
          >
            <CardContent sx={{ display: "grid", gap: 1 }}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                <Chip label={t.folio || "s/folio"} size="small" variant="outlined" />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
                  {t.titulo || "(sin título)"}
                </Typography>
                {t.noLeido && <Chip label="Novedades" size="small" color="warning" />}
              </Box>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                <Chip label={STATUS_LABEL[t.status] || t.status || "—"} size="small" color={STATUS_COLOR[t.status] || "default"} />
                <Chip label={t.prioridad || "MEDIA"} size="small" variant="outlined" />
                <Typography variant="caption" color="text.secondary">
                  {TIPO_LABEL[t.tipoTicket] || t.tipoTicket || ""} · {t.categoria || ""}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ marginLeft: "auto" }}>
                  {getLocalDate(t.fechaActualizacionUtc)} · {t.mensajesCount || 0} mensaje(s)
                </Typography>
              </Box>
              {t.ultimoMensaje && (
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word" }}>
                  {t.ultimoOrigen === "SUPPORT" ? "Soporte: " : ""}{t.ultimoMensaje}
                </Typography>
              )}
              {t.responsableNombre && (
                <Typography variant="caption" color="text.secondary">
                  Atiende: {t.responsableNombre}
                </Typography>
              )}
            </CardContent>
          </Card>
        ))
      )}

      <Dialog open={!!detail} onClose={closeDetail} maxWidth="md" fullWidth>
        <DialogTitle>
          {detail?.folio} · {TIPO_LABEL[detail?.tipoTicket] || detail?.tipoTicket || "Ticket"}
        </DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: 1 }}>
          {detail && (
            <>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                <Chip label={STATUS_LABEL[detail.status] || detail.status} size="small" color={STATUS_COLOR[detail.status] || "default"} />
                <Chip label={detail.prioridad || "MEDIA"} size="small" variant="outlined" />
                <Typography variant="caption" color="text.secondary">
                  {detail.categoria || ""} · Creado {getLocalDate(detail.fechaCreacionUtc)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{detail.titulo}</Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {detail.descripcion || "Sin descripción."}
                </Typography>
              </Box>
              {detailAdjuntos.generales.length > 0 && (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {detailAdjuntos.generales.map((a) => (
                    <Button key={a.adjuntoId} size="small" variant="outlined" onClick={() => downloadAdjunto(detail.ticketId, a)}>
                      ↧ {a.nombreArchivo}
                    </Button>
                  ))}
                </Box>
              )}
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  variant={tab === "conversacion" ? "contained" : "outlined"}
                  onClick={() => setTab("conversacion")}
                >
                  Conversación ({detail.mensajes?.length || 0})
                </Button>
                <Button
                  size="small"
                  variant={tab === "historial" ? "contained" : "outlined"}
                  onClick={() => setTab("historial")}
                >
                  Actualizaciones ({detail.historial?.length || 0})
                </Button>
              </Box>
              {tab === "historial" ? (
                <Box sx={{ display: "grid", gap: 1 }}>
                  {(detail.historial || []).map((h) => (
                    <Box key={h.eventoId} sx={{ borderLeft: "2px solid #565b66", pl: 2 }}>
                      <Typography variant="body2"><strong>{h.actor || ""}</strong></Typography>
                      <Typography variant="body2" color="text.secondary">
                        {h.campo}: {h.anterior || "—"} → {h.nuevo || "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{getLocalDate(h.fechaUtc)}</Typography>
                    </Box>
                  ))}
                  {!(detail.historial || []).length && (
                    <Typography variant="body2" color="text.secondary">
                      Aún no hay actualizaciones registradas para esta solicitud.
                    </Typography>
                  )}
                </Box>
              ) : (
                <Box sx={{ display: "grid", gap: 2 }}>
                  {(detail.mensajes || []).map((m) => (
                    <Box key={m.mensajeId} sx={{ border: "1px solid #41444b", borderRadius: 2, p: 2 }}>
                      <Typography variant="body2">
                        <strong>{m.usuario || "Usuario"}</strong>
                        <Typography component="span" variant="caption" color="text.secondary">
                          {" "}· {m.origen === "SUPPORT" ? "Soporte" : "Mi empresa"} · {getLocalDate(m.fechaUtc)}
                        </Typography>
                        {m.esInterno && <Chip label="NOTA INTERNA" size="small" sx={{ ml: 1 }} />}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", mt: 1 }}>
                        {m.mensaje}
                      </Typography>
                      {m.enlaces && (
                        <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                          Referencia: {m.enlaces}
                        </Typography>
                      )}
                      {(detailAdjuntos.porMensaje.get(m.mensajeId) || []).length > 0 && (
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
                          {detailAdjuntos.porMensaje.get(m.mensajeId).map((a) => (
                            <Button key={a.adjuntoId} size="small" variant="outlined" onClick={() => downloadAdjunto(detail.ticketId, a)}>
                              ↧ {a.nombreArchivo}
                            </Button>
                          ))}
                        </Box>
                      )}
                    </Box>
                  ))}
                  {!(detail.mensajes || []).length && (
                    <Typography variant="body2" color="text.secondary">
                      Inicia la conversación de este ticket.
                    </Typography>
                  )}
                  {detail.status !== "CERRADO" ? (
                    <Box component="form" onSubmit={sendReply} sx={{ display: "grid", gap: 1 }}>
                      <TextField autoComplete="off"
                        label="Añadir un comentario"
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        multiline
                        minRows={3}
                        fullWidth
                        disabled={sending}
                      />
                      <Box>
                        <Typography variant="caption" color="text.secondary" component="label">
                          Adjuntar evidencias (imégenes o PDF, méx. 5 × 8 MB){" "}
                          <input
                            type="file"
                            multiple
                            accept={ACCEPT}
                            disabled={sending}
                            onChange={(e) => {
                              const valid = validateFiles(e.target.files);
                              if (valid) setFiles(valid);
                              e.target.value = "";
                            }}
                          />
                        </Typography>
                        {files.length > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            {files.length} archivo(s): {files.map((f) => f.name).join(", ")}
                          </Typography>
                        )}
                      </Box>
                      <Box>
                        <Button type="submit" variant="contained" disabled={sending || (!reply.trim() && !files.length)}>
                          {sending ? "Enviando..." : "Enviar respuesta"}
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Alert severity="info">
                      Ticket cerrado. Puede crear un nuevo ticket si necesita més ayuda.
                    </Alert>
                  )}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetail}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
